<template>
    <aside id="map-sidebar" class="generic-sub-sidebar">
        <div class="header">
            <div class="title d-flex flex-row align-items-center mb-4">
                <icon class="mr-2" name="map" color="var(--secondary)" scale="0.75" />
                <p class="text-16">{{ $t('keyword49') }}</p>
            </div>
            <div class="d-flex align-items-center justify-content-between">
                <div class="search-input-container mr-2">
                    <icon class="search-icon" name="search" color="var(--secondary-3)" scale=".5" />
                    <input v-model="searchText" class="search-input" :placeholder="$t('keyword167')" />
                </div>
                <div class="dates-filter-container">
                    <ci-button
                        @click="isDatesFilterOpen = !isDatesFilterOpen"
                        :centerIcon="{ name: 'ci-calendar', color: selectedDatesArray.length > 0 ? 'var(--secondary)' : 'white' }"
                        type="primary-1"
                        :class="{ 'filter-active': selectedDatesArray.length > 0 }"
                    />
                    <div v-show="isDatesFilterOpen">
                        <ci-filter-date-select class="dates-filter" :dates="project.capture_dates" v-model="selectedDatesArray" />
                    </div>
                </div>
            </div>
        </div>
        <div class="content d-flex flex-column">
            <div class="gradient-divider"></div>
            <vue-custom-scrollbar ref="scroll">
                <div v-b-toggle="'sectors-list-collapse'" class="collapse-header-btn d-flex flex-row align-items-center pt-1 pb-2">
                    <icon class="mr-2" name="caret-down" color="var(--secondary-3)" :flip="isSectorsListOpen ? 'vertical' : null" />
                    <p class="text-12 text-weight-bold">
                        Setores <span class="text-secondary-3">({{ sectorsQty }})</span>
                    </p>
                </div>
                <b-collapse id="sectors-list-collapse" @show="isSectorsListOpen = true" @hide="isSectorsListOpen = false">
                    <ul class="list mt-2">
                        <p class="text-12 text-weight-bold mb-1 text-center" v-if="filteredSectors.filterTrue.length == 0">
                            {{ $t('Components_Issues_IssueList_20') }}
                        </p>
                        <!-- SECTORS FILTER TRUE -->
                        <li
                            @click="sectorClick(sector)"
                            @mouseenter="sectorHover(sector)"
                            @mouseleave="sectorHover(-1)"
                            v-for="(sector, index) in filteredSectors.filterTrue"
                            :key="index"
                            class="list-item d-flex flex-row align-items-center"
                            :class="{
                                'active-item': sector.id == selectedSector.id,
                            }"
                        >
                            <icon class="mr-2" name="layer-group" color="white" scale=".75" />
                            <p class="text-12">{{ sector.name }}</p>
                        </li>
                        <div class="list-divider"></div>
                        <!-- SECTORS FILTER FALSE -->
                        <li
                            @click="sectorClick(sector)"
                            @mouseenter="sectorHover(sector)"
                            @mouseleave="sectorHover(-1)"
                            v-for="(sector, index) in filteredSectors.filterFalse"
                            :key="'filter-false-' + index"
                            class="list-item filter-false-item d-flex flex-row align-items-center"
                            :class="{
                                'active-item': sector.id == selectedSector.id,
                            }"
                        >
                            <icon class="mr-2" name="layer-group" color="white" scale=".75" />
                            <p class="text-12">{{ sector.name }}</p>
                        </li>
                    </ul>
                </b-collapse>

                <div v-b-toggle="'viewpoints-list-collapse'" class="collapse-header-btn d-flex flex-row align-items-center pt-1 pb-2 mt-4">
                    <icon class="mr-2" name="caret-down" color="var(--secondary-3)" :flip="isViewpointsListOpen ? 'vertical' : null" />
                    <p class="text-12 text-weight-bold">
                        Pontos <span class="text-secondary-3">({{ viewpointsQty }})</span>
                    </p>
                </div>
                <b-collapse id="viewpoints-list-collapse" @show="isViewpointsListOpen = true" @hide="isViewpointsListOpen = false">
                    <ul class="list mt-2">
                        <p class="text-12 text-weight-bold mb-1 text-center" v-if="filteredViewpoints.filterTrue.length == 0">
                            {{ $t('Components_Issues_IssueList_19') }}
                        </p>
                        <!-- VIEWPOINTS FILTER TRUE -->
                        <li
                            @click="mapViewpointClick(viewpoint)"
                            @mouseenter="viewpointHover(viewpoint)"
                            @mouseleave="viewpointHover(-1)"
                            v-for="(viewpoint, index) in filteredViewpoints.filterTrue"
                            :key="index"
                            class="list-item d-flex flex-row align-items-center"
                            :class="{
                                'active-item': viewpoint.id == currentViewpoint.id,
                            }"
                        >
                            <icon class="mr-2" name="ci-circle" scale=".5" />
                            <p class="text-12">{{ viewpoint.name }}</p>
                        </li>
                        <div class="list-divider"></div>
                        <!-- VIEWPOINTS FILTER FALSE -->
                        <li
                            @click="mapViewpointClick(viewpoint)"
                            @mouseenter="viewpointHover(viewpoint)"
                            @mouseleave="viewpointHover(-1)"
                            v-for="(viewpoint, index) in filteredViewpoints.filterFalse"
                            :key="'filter-false-' + index"
                            class="list-item filter-false-item d-flex flex-row align-items-center"
                            :class="{
                                'active-item': viewpoint.id == currentViewpoint.id,
                            }"
                        >
                            <icon class="mr-2" name="ci-circle" scale=".5" />
                            <p class="text-12">{{ viewpoint.name }}</p>
                        </li>
                    </ul>
                </b-collapse>

                <div v-if="project.floorplans.length" @click="selectUnlinkedFloorplans" class="unlinked-floorplans-container">
                    <p class="text-12 text-weight-bold">
                        {{ $t('Components_Viewer_SectorSelectionViewer_1') }} <span class="text-secondary-3 quantity">({{ floorplansQty }})</span>
                    </p>
                </div>
            </vue-custom-scrollbar>
        </div>
    </aside>
</template>

<script>
// ====================
// Icons
// ====================
import 'vue-awesome/icons/door-open'
import 'vue-awesome/icons/caret-down'
import 'vue-awesome/icons/layer-group'
import 'vue-awesome/icons/search'

import VueCustomScrollbar from 'vue-custom-scrollbar'

// Vuex
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    props: {
        /**
         * entire project
         */
        project: {
            type: Object,
        },
        /**
         * sector that user selected (navigation purposes)
         */
        selectedSector: {
            default: -1,
        },
        /**
         * viewpoint that user selected (navigation purposes)
         */
        selectedViewpoint: {
            default: -1,
        },
        /**
         * current sector of the showing viewpoint
         */
        currentSector: {
            default: -1,
        },
        /**
         * current viewpoint that is showing
         */
        currentViewpoint: {
            default: -1,
        },

        curentFloorplan: {
            default: -1,
        },
    },
    components: {
        VueCustomScrollbar,
    },
    data() {
        return {
            isSectorsListOpen: false,
            isViewpointsListOpen: false,
            searchText: '',
            filteredSectors: {
                filterTrue: [],
                filterFalse: [],
            },
            filteredViewpoints: {
                filterTrue: [],
                filterFalse: [],
            },
            filteredUnlinkedFloorplans: {
                filterTrue: [],
                filterFalse: [],
            },
            isDatesFilterOpen: false,
            selectedDatesArray: [],
        }
    },
    computed: {
        ...mapState(['dateFilterViewer']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
        viewpointsQty() {
            if (this.filteredViewpoints.filterFalse.length == 0) {
                return this.filteredViewpoints.filterTrue.length
            } else {
                return (
                    this.filteredViewpoints.filterTrue.length +
                    ' de ' +
                    (this.filteredViewpoints.filterTrue.length + this.filteredViewpoints.filterFalse.length)
                )
            }
        },
        sectorsQty() {
            if (this.filteredSectors.filterFalse.length == 0) {
                return this.filteredSectors.filterTrue.length
            } else {
                return this.filteredSectors.filterTrue.length + ' de ' + (this.filteredSectors.filterTrue.length + this.filteredSectors.filterFalse.length)
            }
        },
        floorplansQty() {
            if (this.filteredUnlinkedFloorplans.filterFalse.length == 0) {
                return this.filteredUnlinkedFloorplans.filterTrue.length
            } else {
                return (
                    this.filteredUnlinkedFloorplans.filterTrue.length +
                    ' de ' +
                    (this.filteredUnlinkedFloorplans.filterTrue.length + this.filteredUnlinkedFloorplans.filterFalse.length)
                )
            }
        },
    },
    watch: {
        dateFilterViewer: {
            handler() {
                this.selectedDatesArray = this.getDatesByProjectId(this.$route.params.id)
            },
            deep: true,
        },
        searchText() {
            // Viewpoints
            this.filterViewpoints()

            // Sectors
            this.filterSectors()

            // Unlinked floorplans
            this.filterUnlinkedFloorplans()
        },
        selectedDatesArray() {
            this.setDates({ projectId: this.$route.params.id, dates: this.selectedDatesArray })
            // Viewpoints
            this.filterViewpoints()

            // Sectors
            this.filterSectors()

            // Unlinked floorplans
            this.filterUnlinkedFloorplans()
        },
    },
    methods: {
        ...mapActions('dateFilterViewer', ['setDates']),
        scrollToTop() {
            this.$refs.scroll.$el.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            })
        },
        // Viewpoints
        mapViewpointClick(viewpoint) {
            this.$emit('mapViewpointClick', viewpoint)
            this.isDatesFilterOpen = false
        },
        viewpointHover(viewpoint) {
            this.$emit('viewpointHover', viewpoint)
        },
        // Sectors
        sectorClick(sector) {
            this.$emit('sectorClick', sector)
            this.isDatesFilterOpen = false
        },
        sectorHover(sector) {
            this.$emit('sectorHover', sector)
        },
        selectUnlinkedFloorplans() {
            this.$emit('sectorClick', {
                id: 'unlinked-floorplans',
                name: this.$t('Components_Viewer_SectorSelectionViewer_1'),
                floorplans: this.filteredUnlinkedFloorplans.filterTrue,
            })
            this.isDatesFilterOpen = false
        },
        // Filtros
        filterViewpoints() {
            this.filteredViewpoints.filterTrue = []
            this.filteredViewpoints.filterFalse = []
            this.project.map.viewpoints.forEach((viewpoint) => {
                if (viewpoint.name.toLowerCase().includes(this.searchText.toLowerCase()) && this.checkViewpointDate(viewpoint)) {
                    this.filteredViewpoints.filterTrue.push(viewpoint)
                } else {
                    this.filteredViewpoints.filterFalse.push(viewpoint)
                }
            })
            this.scrollToTop()
        },
        filterSectors() {
            this.filteredSectors.filterTrue = []
            this.filteredSectors.filterFalse = []
            this.project.map.sectors.forEach((sector) => {
                if (sector.name.toLowerCase().includes(this.searchText.toLowerCase()) && this.checkSectorDate(sector)) {
                    this.filteredSectors.filterTrue.push(sector)
                } else {
                    this.filteredSectors.filterFalse.push(sector)
                }
            })
            this.scrollToTop()
        },
        filterUnlinkedFloorplans() {
            this.filteredUnlinkedFloorplans.filterTrue = []
            this.filteredUnlinkedFloorplans.filterFalse = []
            this.project.floorplans.forEach((floorplan) => {
                if (floorplan.name.toLowerCase().includes(this.searchText.toLowerCase()) && this.checkUnlinkedFloorplanDate(floorplan)) {
                    this.filteredUnlinkedFloorplans.filterTrue.push(floorplan)
                } else {
                    this.filteredUnlinkedFloorplans.filterFalse.push(floorplan)
                }
            })
            this.scrollToTop()
        },
        checkViewpointDate(viewpoint) {
            if (this.selectedDatesArray.length == 0) return true
            let flag = false
            viewpoint.images.forEach((img) => {
                let imgDate = img.date.split(' ')[0]
                this.selectedDatesArray.forEach((date) => {
                    if (date == imgDate) {
                        flag = true
                    }
                })
            })
            return flag
        },
        checkSectorDate(sector) {
            if (this.selectedDatesArray.length == 0) return true
            let flag = false
            sector.floorplans.forEach((fp) => {
                fp.viewpoints.forEach((vp) => {
                    vp.images.forEach((img) => {
                        let imgDate = img.date.split(' ')[0]
                        this.selectedDatesArray.forEach((date) => {
                            if (date == imgDate) {
                                flag = true
                            }
                        })
                    })
                })
            })
            return flag
        },
        checkUnlinkedFloorplanDate(floorplan) {
            if (this.selectedDatesArray.length == 0) return true
            let flag = false
            floorplan.viewpoints.forEach((vp) => {
                vp.images.forEach((img) => {
                    let imgDate = img.date.split(' ')[0]
                    this.selectedDatesArray.forEach((date) => {
                        if (date == imgDate) {
                            flag = true
                        }
                    })
                })
            })
            return flag
        },
    },
    mounted() {
        this.filteredViewpoints.filterTrue = this.project.map.viewpoints
        this.filteredSectors.filterTrue = this.project.map.sectors
        this.filteredUnlinkedFloorplans.filterTrue = this.project.floorplans
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/generic-sub-sidebar.scss';
@import '@/scss/components/360Viewer/Sidebar/Floorplans/map-sidebar.scss';
</style>
